/**
 * mapLiveness.ts — is this MapLibre map still usable? (T-071 / BEAD-0037)
 *
 * The sign-out crash: when the whole map subtree unmounts, MapView's cleanup runs
 * `map.remove()`, which sets `map._removed = true` and drops `map.style`. React then runs
 * the child layer components' cleanups (CountyLayer/TractLayer/RoadsLayer/EVChargerLayer/
 * POILayerMgr), each of which calls `map.getLayer(id)` to decide whether to remove its
 * layer. But `getLayer` dereferences the now-undefined `map.style` and throws
 * `TypeError: Cannot read properties of undefined (reading 'getLayer')`. Uncaught in a
 * passive-unmount effect, that tears down the React root → blank page (or, with the
 * ErrorBoundary, the error fallback) instead of the login screen.
 *
 * A layer's own removeLayer/removeSource is redundant once the map is being destroyed —
 * `map.remove()` tears the whole style down anyway. So the fix is: skip the teardown when
 * the map is already gone. This helper is that guard.
 *
 * It checks two independent signals so a rename of either private in a future maplibre-gl
 * still leaves the other guarding:
 *   - `_removed` — set to true inside `Map.remove()` (maplibre-gl 5.x).
 *   - `style`    — the Style instance, undefined after removal (what actually makes
 *                  getLayer/getSource throw).
 * A live, loaded map has `_removed === false` and a truthy `style`.
 */
import type { Map as MapLibreMap } from 'maplibre-gl'

type RemovableMap = MapLibreMap & { _removed?: boolean; style?: unknown }

/**
 * True when `map` is still safe to call getLayer/getSource/removeLayer against.
 * False once `map.remove()` has run (or the map is nullish), so callers can no-op their
 * own layer/source teardown during full map destruction.
 */
export function isMapAlive(map: MapLibreMap | null | undefined): boolean {
  if (!map) return false
  const m = map as RemovableMap
  if (m._removed === true) return false
  if (!m.style) return false
  return true
}
